import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

export default function RHFAutocomplete({
  name,
  label,
  placeholder,
  helperText,
  options,
  multiple = false,
  customFunction,
  renderOption: customRenderOption,
  ...other
}) {
  const { control, setValue } = useFormContext();

  const findOptionByValue = (value) => {
    if (multiple) {
      return Array.isArray(value)
        ? value.map((v) =>
            typeof v === 'object' ? v : options.find((option) => option.value === v) || v
          )
        : [];
    }

    if (typeof value === 'object' && value !== null) {
      return value;
    }

    return options.find((option) => option.value === value) ?? null;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          multiple={multiple}
          options={options}
          getOptionLabel={(option) => option.label || option}
          isOptionEqualToValue={(option, value) => {
            if (typeof value === 'object') return option.value === value.value;
            return option.value === value;
          }}
          value={findOptionByValue(field.value)}
          onChange={(event, newValue) => {

            let value;
            if (multiple) {
              value = newValue ? newValue.map((item) =>  (item && typeof item === 'object') ? item.value : item) : [];
            } else {
              value = newValue && typeof newValue === 'object' ? newValue.value : newValue;
            }
            setValue(name, value, { shouldValidate: true });
            if (customFunction) {
              customFunction();
            }
          }}
          renderOption={
            customRenderOption ||
            ((props, option) => (
              <li
                {...props}
                key={typeof option === 'object' ? option.value : option}
                style={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  lineHeight: '1.4',
                  padding: '12px 16px',
                  minHeight: 'auto',
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                {option.label || option}
              </li>
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error?.message : helperText}
            />
          )}
          ListboxProps={{
            style: {
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              overflowY: 'scroll',
            },
            sx: {
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              '& .MuiAutocomplete-option': {
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: 1.4,
                minHeight: 'auto',
                height: 'auto',
                padding: '12px 16px',
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              },
            },
          }}
          {...other}
        />
      )}
    />
  );
}

RHFAutocomplete.propTypes = {
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  customFunction: PropTypes.func,
  renderOption: PropTypes.func,
};
